// src/templates/MetaspacePage/MetaspacePage.tsx

import React, { useEffect, useLayoutEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Header from '../Header/Header';
import { useSelector, useDispatch } from 'react-redux';
import { selectSphereId } from '@center-pwa/selectors/selectors';
import type { RootState } from '@center-pwa/store/store';
import MetaspaceContent from '../MetaspaceContent/MetaspaceContent';
// Import the action that sets the sphereId in your Redux store
// Adjust the import path to your actual file
import { setSphereId } from '@center-pwa/reducers/sphereReducer';
import { clearEvents } from '@center-pwa/reducers/metaspaceEventsReducer';
import { selectAllMetaspaces } from '@center-pwa/reducers/metaspacesReducer';
import { MobileNavBar, MobileNotificationBell, MobileHeader, MetaspaceToolbarMobile, useApiClientsOrNull, useMediaQuery, LoaderMolecule } from 'design-system/my-lib';
import { useTranslation } from 'localization';
import './MetaspacePage.scss';
import logo from '../../../assets/images/center-icon.png';
import { getSphereById } from '@center-platform/spheres/sphere';
import type { Sphere } from 'types/global';

interface MetaspacePageProps {
  onMetaspaceIdChange: (newMetaspaceId: string) => void;
}

const MetaspacePage: React.FC<MetaspacePageProps> = ({ onMetaspaceIdChange }) => {
  // Destructure the current :sphereId and :metaspaceId from route params
  const { sphereId, metaspaceId } = useParams<{ sphereId: string; metaspaceId: string }>();

  // Read the current Redux sphereId
  const selectedSphereId = useSelector((state: RootState) => selectSphereId(state));

  // Read locale from Redux or fallback to 'en-US'
  const userLocale = useSelector((state: RootState) => state.userProfile?.locale ?? 'en-US');

  // For dispatching actions
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // API clients and sphere state
  const apiClients = useApiClientsOrNull();
  const graphClient = apiClients?.graphClient;
  const [selectedSphere, setSelectedSphere] = useState<Sphere | null>(null);

  const { t } = useTranslation();

  // Mobile detection
  const isMobile = useMediaQuery('(max-width: 767px)');

  // MobileHeader is `display: none` above 500px (MobileHeaderOrganism.scss), but it was
  // still being *mounted* on desktop — and it renders its own UserSphere, so every
  // desktop load and every sphere change fetched the breadcrumb's hierarchy tree and
  // ancestors twice, for a header nobody could see. Match the mount to its stylesheet.
  const showMobileHeader = useMediaQuery('(max-width: 500px)');

  // Mobile toolbar state
  const [toolbarOpen, setToolbarOpen] = useState(false);

  // Which sphere the header (identity + sphere breadcrumb) is fully populated for.
  // Gates the metaspace so the page loads top-down rather than the metaspace racing the
  // chrome. Sphere-keyed, not a boolean: a boolean left over from the previous user
  // still read as "ready" for the instant after the new sphere landed, which mounted the
  // metaspace early and then reloaded it once the header caught up.
  const [headerReadySphereId, setHeaderReadySphereId] = useState<string | null>(null);

  // Close chat toolbar when navigating between tabs
  useEffect(() => {
    setToolbarOpen(false);
    // Clear Redux events to prevent stale events from reopening toolbar
    dispatch(clearEvents());
  }, [location.pathname, dispatch]);

  // Fetch sphere data
  useEffect(() => {
    async function fetchSphere() {
      if (!selectedSphereId || !graphClient) return;

      try {
        const resp = await getSphereById(graphClient, selectedSphereId, false);
        setSelectedSphere(resp.data.sphere);
      } catch (err) {
        console.error('Error fetching sphere:', err);
      }
    }

    fetchSphere();
  }, [selectedSphereId, graphClient]);

  // Handle "View All Notifications" click
  const handleViewAllNotifications = useCallback(() => {
    // Navigate to notifications page (adjust route as needed)
    navigate('/notifications');
  }, [navigate]);

  // Handle Atlas/Metasphere click
  const handleAtlasClick = useCallback(() => {
    if (selectedSphereId) {
      navigate(`/sphere/${selectedSphereId}/metaspace/00000000-0000-0000-0000-000000000001`);
    }
  }, [selectedSphereId, navigate]);


  // Settings navigation logic (moved from MobileHeader)
  const userId = useSelector((state: RootState) => state.userProfile?.userId);
  const metaspaces = useSelector(selectAllMetaspaces);
  const handleSettingsMenuClick = useCallback(() => {
    if (!userId) return;
    const identityMetaspace = metaspaces?.find(
      (ms: any) => ms.system_name === 'center_identity_management_metaspace'
    );
    if (identityMetaspace?.metaspace_id) {
      navigate(`/sphere/${userId}/metaspace/${identityMetaspace.metaspace_id}`);
    } else {
      console.error('Identity Management metaspace not found in loaded metaspaces');
    }
  }, [userId, metaspaces, navigate]);


  // Determine active nav item based on current route
  const identityMetaspaceId = metaspaces?.find(
    (ms: any) => ms.system_name === 'center_identity_management_metaspace'
  )?.metaspace_id;

  const isSettingsActive = identityMetaspaceId === metaspaceId;

  // Atlas should be active for any metaspace view EXCEPT Identity Management (settings)
  const isAtlasActive = !!metaspaceId && !!sphereId && !isSettingsActive;

  // Handle chevron button toggle
  const handleChevronToggle = useCallback(() => {
    setToolbarOpen(!toolbarOpen);
  }, [toolbarOpen]);

  // Render the metaspace only once the header is populated *for the very sphere we are
  // about to render*. Both halves are required: the page's own sphere fetch must have
  // caught up (`selectedSphere`), and the header must report readiness for that same id.
  // Comparing ids rather than trusting a boolean is what makes this hold across a user
  // switch, where every signal briefly still describes the previous user.
  const headerSphereMatches = !!selectedSphere && selectedSphere.id === selectedSphereId;
  const showMetaspace =
    !!selectedSphereId && headerReadySphereId === selectedSphereId && headerSphereMatches;

  // Memoize the notification bell component to avoid recreating on every render
  const notificationBellComponent = useMemo(
    () => <MobileNotificationBell onViewAllClick={handleViewAllNotifications} />,
    [handleViewAllNotifications]
  );

  const navItems = useMemo(() => [
    {
      id: 'home',
      icon: 'Home-icon',
      label: 'Home',
      onClick: () => navigate('/'),
      isActive: location.pathname === '/'
    },
    {
      id: 'atlas',
      icon: 'Atlas-icon',
      label: 'Atlas',
      onClick: handleAtlasClick,
      isActive: isAtlasActive
    },
    {
      id: 'chevron',
      label: 'Chat',
      isChevron: true,
      chevronExpanded: toolbarOpen,
      onClick: handleChevronToggle,
      badge: 0
    },
    {
      id: 'notifications',
      label: 'Notifications',
      customRender: () => notificationBellComponent
    },
    {
      id: 'menu',
      icon: 'Settings-icon',
      label: 'Menu',
      onClick: handleSettingsMenuClick,
      isActive: isSettingsActive
    }
  ], [navigate, location.pathname, handleAtlasClick, isAtlasActive, toolbarOpen, handleChevronToggle, handleViewAllNotifications, handleSettingsMenuClick, isSettingsActive]);
  /**
   * Keep Redux sphereId in sync with the route param sphereId.
   *
   * useLayoutEffect, not useEffect: children (MetaspaceContent → MetaspaceGrid) gate
   * their mount on the Redux sphereId, and child effects run before the parent's.
   * Committing the route sphere in a layout effect flushes the resulting re-render
   * before paint, so the grid mounts once with its final key instead of showing an
   * empty frame first.
   */
  useLayoutEffect(() => {
    if (sphereId && sphereId !== selectedSphereId) {
      dispatch(setSphereId({ sphereId, source: 'MetaspacePage' }));
    }
  }, [sphereId, selectedSphereId, dispatch]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {showMobileHeader && (
        <MobileHeader
          logoSrc={logo}
          logoAlt="Center Logo"
          onLogoClick={() => navigate('/')}
          identityId={selectedSphere?.id}
          sphere={selectedSphere}
          metaspaceId={metaspaceId ?? ''}
          onSphereChange={(newSphereId: string) => {
            if (newSphereId && metaspaceId) {
              navigate(`/sphere/${newSphereId}/metaspace/${metaspaceId}`);
            }
          }}
          onMetaspaceSelected={(metaspaceId: string) => {
            if (selectedSphere?.id && metaspaceId) {
              navigate(`/sphere/${selectedSphere.id}/metaspace/${metaspaceId}`);
            }
          }}
        />
      )}
      <Header
        sphereId={selectedSphereId}
        defaultLanguage={userLocale}
        metaspaceId={metaspaceId ?? ''}
        sphere={selectedSphere}
        onReadyChange={setHeaderReadySphereId}
      />
      {/* Load header-first: the metaspace is expensive and used to paint while the
          identity selector and sphere breadcrumb above it were still resolving, so the
          page assembled out of order. Holding it until the header reports ready costs
          nothing (the grid mounts once either way, just later) and the page fills in
          top-down. */}
      {showMetaspace ? (
        <MetaspaceContent
          onMetaspaceIdChange={onMetaspaceIdChange}
          metaspaceId={metaspaceId ?? ''}
          toolbarOpen={toolbarOpen}
          onToolbarOpenChange={setToolbarOpen}
        />
      ) : (
        <div className="metaspace-pending">
          <LoaderMolecule variant="inline" caption={t('common.loader.metaspace')} />
        </div>
      )}
      {isMobile && (
        <MetaspaceToolbarMobile
          isOpen={toolbarOpen}
          onOpenChange={setToolbarOpen}
          chatSphereId={selectedSphereId}
        />
      )}
      <MobileNavBar items={navItems} position="bottom" />
    </div>
  );
};

export default MetaspacePage;
